import { Switch } from '@components/core-ui/form-elements/Switch';
import { Tag } from '@components/core-ui/Tag';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { createUpdateJobNoShowMutationFn } from '../../../../../../api/updateJobNoShow';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import LoadingIcon from '@components/icons/Loading.svg';
import { JobStatusText } from '@components/JobStatusText';
import { KeyTermsList } from '@components/KeyTermsList';
import { TagMuted } from '@components/core-ui/Tag/variations/TagMuted';
import { canChooseCloudLayout } from '@helpers/canChooseCloudLayout';
import { hasVersionsBeyondLayoutDraft } from '@helpers/hasVersionsBeyondLayoutDraft';
import { convertDuration } from '@helpers/convertDuration';
import { formatIsoDateToAmerican } from '@helpers/formatIsoDateToAmerican';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { getTranscriptUnlockTypeName } from '@helpers/getTranscriptUnlockTypeName';
import { SpeakerLabelOverride } from '@helpers/buildSpeakerLabelOverrides';
import { openCloudLayoutRegenerationFlow } from '@helpers/openCloudLayoutRegenerationFlow';
import { shouldShowSpeakerLabelOverrides } from '@helpers/shouldShowSpeakerLabelOverrides';
import { SpeakerLabellingScoreTag } from './helpers/speakerLabellingScoreTag';
import { Tooltip } from '@components/core-ui/Tooltip';
import QuestionIcon from '@components/icons/QuestionIcon.svg';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobsAdminService } from 'app/API';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { createFetchOrganizationLayoutOptionsQueryFn } from '../../../../../../api/fetchOrganizationLayoutOptions';
import { usePageContext } from '../../../../../../providers/PageProvider';
import { InfoBlock } from '../../styles/InfoBlock';
import { InfoBlockContent } from '../../styles/InfoBlockContent';
import { InfoBlockTitle } from '../../styles/InfoBlockTitle';
import { InfoBlockWrapper } from '../../styles/InfoBlockWrapper';
import { JobSection } from '../../styles/JobSection';
import { JobSectionLeft } from '../../styles/JobSectionLeft';
import { JobSectionRight } from '../../styles/JobSectionRight';
import { StyledLink } from '../../styles/StyledLink';
import { DeleteJobButton } from '../DeleteJobButton';
import { AssigneeBlockContent } from './styles/AssigneeBlockContent';
import { JobRightRow } from './styles/JobRightRow';

export const GeneralSection = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const noShowMutation = useMutation({
    mutationFn: createUpdateJobNoShowMutationFn(call),
    onError: (error) => toast.error(getApiErrorMessage(error) || 'Unable to update the No Show attribute.'),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: adminJobsKeys.all });
    },
  });
  const { openModal, closeModal } = useModalContext();
  const { job, manualReload } = usePageContext();
  const [layoutId, setLayoutId] = useState<string>(job?.layoutId || '');
  const keyTerms = (job?.keyTerms ?? []).filter((keyTerm) => !!keyTerm);
  const averagePipelineAudioScore = useMemo(() => {
    const scores =
      job?.media
        ?.map((media) => media.audioQualityScore?.proxyScore ?? media.AudioQualityScore?.proxyScore)
        .filter((score): score is number => typeof score === 'number' && Number.isFinite(score)) ?? [];

    if (!scores.length) {
      return null;
    }

    return scores.reduce((total, score) => total + score, 0) / scores.length;
  }, [job?.media]);

  const organizationId = job?.organization?.id ?? '';
  const layoutOptionsQuery = useQuery({
    queryKey: layoutsKeys.optionsByOrganization(organizationId),
    queryFn: createFetchOrganizationLayoutOptionsQueryFn(call),
    enabled: organizationId !== '',
  });
  const layoutsList = useMemo(
    () =>
      (layoutOptionsQuery.data?.data ?? []).map((layout: { id: string; name: string }) => ({
        label: layout.name,
        value: layout.id,
      })),
    [layoutOptionsQuery.data],
  );

  useEffect(() => {
    setLayoutId(job?.layoutId || '');
  }, [job?.layoutId]);

  const regenerateMutation = useMutation({
    mutationFn: ({ jobId, speakerLabelOverrides }: { jobId: string; speakerLabelOverrides?: SpeakerLabelOverride[] }) =>
      call(
        () =>
          JobsAdminService.adminRegenerateWithNewLayout({
            jobId,
            requestBody: { layoutId, ...(speakerLabelOverrides ? { speakerLabelOverrides } : {}) },
          }),
        { onError: (message) => toast.error(message) },
      ),
    onSuccess: () => {
      toast.success('Regeneration started');
      manualReload();
    },
  });

  const isCloudLayoutChoiceAvailable = canChooseCloudLayout(job?.status, job?.versions);

  const regenerateWithLayout = () => {
    if (!job || !layoutId) {
      return;
    }

    void openCloudLayoutRegenerationFlow({
      openModal,
      closeModal,
      hasVersions: hasVersionsBeyondLayoutDraft(job.layoutId, job.versions?.length ?? 0),
      speakerLabels: shouldShowSpeakerLabelOverrides(job.transcriptUnlockType) ? job.speakerLabels ?? [] : [],
      submit: (speakerLabelOverrides) => regenerateMutation.mutateAsync({ jobId: job.id, speakerLabelOverrides }),
    });
  };

  if (!job) {
    return null;
  }

  return (
    <JobSection>
      <JobSectionLeft>General</JobSectionLeft>
      <JobSectionRight>
        <JobRightRow>{job.name}</JobRightRow>
        <InfoBlockWrapper>
          <InfoBlock>
            <InfoBlockTitle>Job ID</InfoBlockTitle>
            <InfoBlockContent>{job.id}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Status</InfoBlockTitle>
            <InfoBlockContent className="flex items-center gap-3">
              <JobStatusText status={job.status} />
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Recording Session Type</InfoBlockTitle>
            <InfoBlockContent className="flex items-center gap-3">
              <div>{job.meetingType?.replace('RECORDING_SESSION_', '') || '---'}</div>
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Delete</InfoBlockTitle>
            <DeleteJobButton className="!min-w-[100px]" />
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>No Show</InfoBlockTitle>
            <InfoBlockContent className="flex items-center gap-3">
              <Switch
                label="No Show"
                checked={job.noShow}
                disabled={noShowMutation.isPending}
                onChange={() => noShowMutation.mutate({ jobId: job.id, noShow: !job.noShow })}
              />
              {job.noShow ? <Tag>Yes</Tag> : <TagMuted>No</TagMuted>}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Rush Job</InfoBlockTitle>
            <InfoBlockContent>{job.rushed ? 'Yes' : 'No'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Job Duration</InfoBlockTitle>
            <InfoBlockContent>{job.duration ? `${convertDuration(job.duration)} (hh:mm:ss)` : '---'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Uploaded Files</InfoBlockTitle>
            <InfoBlockContent>{job.media?.length ?? job.filesCount ?? 0}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Workspace</InfoBlockTitle>
            <InfoBlockContent>{job.workspaceName || job.workspaceSlug || job.workspaceId || '---'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Organization</InfoBlockTitle>
            <InfoBlockContent>
              {job.organization?.id && (
                <StyledLink to={routes.adminOrganizationDetails.make(job.organization.id)}>
                  {job.organization?.name || '---'}
                </StyledLink>
              )}
            </InfoBlockContent>
          </InfoBlock>

          {job.bucket ? (
            <InfoBlock>
              <InfoBlockTitle>S3 Bucket</InfoBlockTitle>
              <InfoBlockContent>
                <a
                  href={`https://s3.console.aws.amazon.com/s3/buckets/${job.bucket}?region=us-east-1&prefix=jobs/${job.id}/`}
                  rel="noreferrer"
                  target="_blank"
                  className="underline hover:no-underline"
                >
                  Link
                </a>
              </InfoBlockContent>
            </InfoBlock>
          ) : null}

          <InfoBlock>
            <InfoBlockTitle>Version Count</InfoBlockTitle>
            <InfoBlockContent>{job.versions?.length ?? 0}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Last version link</InfoBlockTitle>
            <InfoBlockContent>
              {job.versions?.length ? (
                <a
                  href={`https://s3.console.aws.amazon.com/s3/buckets/${job.bucket}?region=us-east-1&prefix=jobs/${job.id}/versions/${job.versions[0].id}/`}
                  rel="noreferrer"
                  target="_blank"
                  className="underline hover:no-underline"
                >
                  Link
                </a>
              ) : (
                '---'
              )}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Transcript Unlock Type</InfoBlockTitle>
            <InfoBlockContent className="align-middle flex gap-2">
              {getTranscriptUnlockTypeName(job.transcriptUnlockType)}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Partial Unlock</InfoBlockTitle>
            <InfoBlockContent>{job.partialUnlocked ? 'Yes' : 'No'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Processed Audio</InfoBlockTitle>
            <InfoBlockContent>{job.processedAudioFilename || '---'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Playback Audio</InfoBlockTitle>
            <InfoBlockContent>{job.playbackAudioFilename || '---'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Date created</InfoBlockTitle>
            <InfoBlockContent>{getAmericanTime(job.createdAt)}</InfoBlockContent>
          </InfoBlock>

          {job.deadline ? (
            <InfoBlock>
              <InfoBlockTitle>Deadline</InfoBlockTitle>
              <InfoBlockContent>{formatIsoDateToAmerican(job.deadline)}</InfoBlockContent>
            </InfoBlock>
          ) : null}

          <InfoBlock>
            <InfoBlockTitle>Assignee</InfoBlockTitle>
            <AssigneeBlockContent>
              {job?.assignee ? (
                <div className="assignee">
                  <StyledLink to={routes.adminUserDetails.make(job?.assignee?.id)}>
                    <p className="assignName">
                      {job?.assignee?.name} {job?.assignee?.lastname}
                    </p>
                    <p className="assignEmail">{job?.assignee?.email || ''}</p>
                  </StyledLink>
                </div>
              ) : null}
            </AssigneeBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Cloud Layout</InfoBlockTitle>
            {isCloudLayoutChoiceAvailable ? (
              <div className="flex flex-col gap-2">
                <AutoCompleteSelect
                  name="layoutId"
                  label="Layout"
                  value={layoutId}
                  onChange={(_, value) => setLayoutId(value)}
                  options={layoutsList}
                  required={false}
                  disabled={!layoutsList.length}
                />
                {layoutId && job.layoutId !== layoutId ? (
                  <OutlinedButton
                    onClick={regenerateWithLayout}
                    disabled={regenerateMutation.isPending}
                    className="flex items-center justify-center gap-2"
                  >
                    {regenerateMutation.isPending ? <LoadingIcon className="h-4 w-4 shrink-0 animate-spin" /> : null}
                    {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate with layout'}
                  </OutlinedButton>
                ) : null}
              </div>
            ) : (
              <InfoBlockContent>{job.layoutName || 'None'}</InfoBlockContent>
            )}
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Transcription confidence</InfoBlockTitle>
            <InfoBlockContent>
              {job?.transcriptConfidence ? (
                <StyledLink to={routes.adminJobUtterances.make(job?.id)}>
                  {`~${(Number(job.transcriptConfidence ?? 0) * 100).toFixed(2)} %`}
                </StyledLink>
              ) : (
                '---'
              )}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Average pipeline audio score</InfoBlockTitle>
            <InfoBlockContent>
              {averagePipelineAudioScore === null ? '---' : `${averagePipelineAudioScore.toFixed(1)} / 5`}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Provider Transcript ID</InfoBlockTitle>
            <InfoBlockContent>{job.providerTranscriptId || '---'}</InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>ASR Proxy request</InfoBlockTitle>
            <InfoBlockContent>
              {job.asrProxyTranscriptId ? (
                <StyledLink to={routes.adminAsrProxyTranscription.make(job.asrProxyTranscriptId)}>
                  {job.asrProxyTranscriptId}
                </StyledLink>
              ) : (
                '---'
              )}
            </InfoBlockContent>
          </InfoBlock>

          {job.asrProxyTranscriptId ? (
            <InfoBlock>
              <InfoBlockTitle>ASR Provider</InfoBlockTitle>
              <InfoBlockContent>{job.asrProxyProvider || '---'}</InfoBlockContent>
            </InfoBlock>
          ) : null}

          {job.asrProxyTranscriptId ? (
            <InfoBlock>
              <InfoBlockTitle>ASR Model</InfoBlockTitle>
              <InfoBlockContent>{job.asrProxyModel || '---'}</InfoBlockContent>
            </InfoBlock>
          ) : null}

          <InfoBlock>
            <InfoBlockTitle>ASR proxy attempts</InfoBlockTitle>
            <InfoBlockContent>
              <Link className="btn btn--outlined inline-flex w-auto" to={routes.adminAsrProxyJobsSearch.make(job.id)}>
                Show all attempts
              </Link>
            </InfoBlockContent>
          </InfoBlock>

          {shouldShowSpeakerLabelOverrides(job.transcriptUnlockType) && (
            <InfoBlock>
              <InfoBlockTitle>ASOne Speaker Labeling score</InfoBlockTitle>
              <InfoBlockContent className="flex items-center gap-3">
                <SpeakerLabellingScoreTag score={job.speakerLabellingQualityScore} />
                {job.speakerLabellingQualityNotes && (
                  <Tooltip content={job.speakerLabellingQualityNotes} placement="bottom">
                    <QuestionIcon />
                  </Tooltip>
                )}
              </InfoBlockContent>
            </InfoBlock>
          )}

          <InfoBlock>
            <InfoBlockTitle>Editing Progress</InfoBlockTitle>
            <InfoBlockContent>
              {job.editingProgress !== undefined && job.editingProgress !== null ? `${job.editingProgress}%` : '---'}
            </InfoBlockContent>
          </InfoBlock>

          <InfoBlock>
            <InfoBlockTitle>Job cloned from:</InfoBlockTitle>
            <InfoBlockContent>
              {job.clonedFromJobId && job.clonedFromEnvironment
                ? `${job.clonedFromJobId} (${job.clonedFromEnvironment})`
                : '---'}
            </InfoBlockContent>
          </InfoBlock>

          {job.tags?.length ? (
            <InfoBlock>
              <InfoBlockTitle>Tags</InfoBlockTitle>
              <InfoBlockContent>
                <div className="flex flex-wrap gap-2">
                  {job.tags.map((tag: string) => (
                    <TagMuted key={tag}>{tag}</TagMuted>
                  ))}
                </div>
              </InfoBlockContent>
            </InfoBlock>
          ) : null}

          {keyTerms.length ? (
            <InfoBlock>
              <InfoBlockTitle>Keyterms</InfoBlockTitle>
              <InfoBlockContent>
                <KeyTermsList keyTerms={keyTerms} />
              </InfoBlockContent>
            </InfoBlock>
          ) : null}
        </InfoBlockWrapper>
      </JobSectionRight>
    </JobSection>
  );
};
